import DOMPurify from 'dompurify';
import { closedNotificationsKey } from '@/infrastructure/adapters/constants';
import {
  RequestForNotificationHydraItem,
  RequestForNotificationHydraItemLocation
} from '@/infrastructure/download-client/download-client';

export interface IClosedNotification {
  notificationId: string;
  closedAt: Date;
}

const notificationPurifier = DOMPurify();

notificationPurifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export function sanitizeNotificationContent(content?: string): string {
  return notificationPurifier.sanitize(content ?? '', {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'h1', 'h2', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  });
}

export function getVisibleNotifications(
  notifications: RequestForNotificationHydraItem[],
  location: RequestForNotificationHydraItemLocation,
  productId?: number
): RequestForNotificationHydraItem[] {
  const closed = getClosedNotifications();

  return notifications.filter((notification) => {
    const isWholeSite = notification.location === RequestForNotificationHydraItemLocation.WholeSite;
    const isMatchingLocation = notification.location === location;

    if (!isWholeSite && !isMatchingLocation) {
      return false;
    }

    if (
      location === RequestForNotificationHydraItemLocation.ProductPage &&
      !isWholeSite &&
      !notification.producten?.some((product) => product.productId === productId)
    ) {
      return false;
    }

    const closedNotification = closed.find((item) => item.notificationId === notification.notificationId);

    return (
      !closedNotification ||
      (!!notification.datumGewijzigd && new Date(notification.datumGewijzigd) > new Date(closedNotification.closedAt))
    );
  });
}

export function getClosedNotifications(): IClosedNotification[] {
  return JSON.parse(localStorage.getItem(closedNotificationsKey) || '[]');
}

export function saveClosedNotifications(closedNotifications: IClosedNotification[]): void {
  localStorage.setItem(closedNotificationsKey, JSON.stringify(closedNotifications));
}

export function markNotificationClosed(notificationId: string, closedAt: string): void {
  const closedNotifications = getClosedNotifications();
  const index = closedNotifications.findIndex((n) => n.notificationId === notificationId);

  if (index === -1) {
    closedNotifications.push({ notificationId: notificationId, closedAt } as unknown as IClosedNotification);
  } else {
    closedNotifications[index].closedAt = closedAt as unknown as Date;
  }

  saveClosedNotifications(closedNotifications);
}

export function cleanClosedNotifications(notifications: RequestForNotificationHydraItem[]): void {
  const closedNotifications = getClosedNotifications();

  const updatedClosedNotifications = closedNotifications.filter((closedNotification) => {
    const notification = notifications.find((n) => n.notificationId === closedNotification.notificationId);
    return notification !== undefined;
  });

  saveClosedNotifications(updatedClosedNotifications);
}
